/* =========================================================
   NOVARIX — Core JS
   Module 01: namespace, utils, base DOM setup
   Module 02: header scroll, mega menu, mobile nav drawer
   (Cart/search/product behavior added by later modules)
   ========================================================= */

document.documentElement.classList.remove("no-js");
document.documentElement.classList.add("js");

window.Novarix = window.Novarix || {};

Novarix.utils = {
  debounce(fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  },

  throttle(fn, limit) {
    let inThrottle = false;
    return function (...args) {
      if (!inThrottle) {
        fn.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  },

  trapFocus(container, initialFocusEl) {
    const focusableSelectors = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusable = container.querySelectorAll(focusableSelectors);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function handleKeydown(e) {
      if (e.key !== "Tab") return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    container.addEventListener("keydown", handleKeydown);
    (initialFocusEl || first).focus();

    return function removeTrap() {
      container.removeEventListener("keydown", handleKeydown);
    };
  },

  formatMoney(cents, format) {
    const value = (cents / 100).toFixed(2);
    return (format || "${{amount}}").replace("{{amount}}", value);
  }
};

/* ---------- Generic drawer helpers (reused by cart/search in later modules) ---------- */
Novarix.openDrawer = function (drawer, overlaySelector) {
  if (!drawer) return;
  drawer.classList.add("is-open");
  if (overlaySelector) document.querySelector(overlaySelector)?.classList.add("is-open");
  document.body.classList.add("no-scroll");
};
Novarix.closeDrawer = function (drawer, overlaySelector) {
  if (!drawer) return;
  drawer.classList.remove("is-open");
  if (overlaySelector) document.querySelector(overlaySelector)?.classList.remove("is-open");
  document.body.classList.remove("no-scroll");
};

document.addEventListener("DOMContentLoaded", function () {
  document.body.classList.add("dom-ready");
  initHeaderScroll();
  initMegaMenu();
  initHeroShopDropdown();
  initMobileNav();
  initReveal();
  initWishlist();
  initCollectionSort();
  initFilterDrawer();
  initProductForm();
  initPaymentSelector();
  initGalleryThumbs();
  initQuantitySelectors();
  initAccordions();
  initStickyAddToCart();
  initSizeGuide();
  initProductRecommendations();
  initRecentlyViewed();
  initCountdown();
  initProductCardSwatches();
  initQuickAddSizePicker();
  initLoadMore();
});

/* ---------- Wishlist toggle (visual + persisted, product card) ---------- */
function initWishlist() {
  const KEY = "novarix_wishlist";
  let saved = [];
  try {
    saved = JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch (e) {
    saved = [];
  }

  document.querySelectorAll("[data-wishlist-btn]").forEach((btn) => {
    const id = btn.dataset.wishlistBtn;
    if (saved.includes(id)) btn.classList.add("is-active");

    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const list = new Set(JSON.parse(localStorage.getItem(KEY) || "[]"));
      if (list.has(id)) {
        list.delete(id);
        btn.classList.remove("is-active");
      } else {
        list.add(id);
        btn.classList.add("is-active");
      }
      localStorage.setItem(KEY, JSON.stringify([...list]));
    });
  });
}

/* ---------- Collection sort (navigates with sort_by param) ---------- */
function initCollectionSort() {
  document.querySelectorAll("[data-sort-select]").forEach((select) => {
    select.addEventListener("change", () => {
      const url = new URL(window.location.href);
      url.searchParams.set("sort_by", select.value);
      window.location.href = url.toString();
    });
  });
}

/* ---------- Mobile filters drawer (collection page) ---------- */
function initFilterDrawer() {
  const openBtn = document.querySelector("[data-filter-drawer-open]");
  const closeBtns = document.querySelectorAll("[data-filter-drawer-close]");
  const drawer = document.querySelector("[data-filter-drawer]");
  const overlay = document.querySelector('[data-overlay="filters"]');
  if (!drawer) return;

  openBtn?.addEventListener("click", () => Novarix.openDrawer(drawer, '[data-overlay="filters"]'));
  closeBtns.forEach((btn) => btn.addEventListener("click", () => Novarix.closeDrawer(drawer, '[data-overlay="filters"]')));
  overlay?.addEventListener("click", () => Novarix.closeDrawer(drawer, '[data-overlay="filters"]'));
}

/* ---------- Reveal on scroll (used by category grid, editorial, etc.) ---------- */
function initReveal() {
  const items = document.querySelectorAll(".reveal");
  if (!items.length || !("IntersectionObserver" in window)) {
    items.forEach((el) => el.classList.add("is-visible"));
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12 }
  );
  items.forEach((el) => observer.observe(el));
}

/* ---------- Header scroll behavior ---------- */
function initHeaderScroll() {
  const header = document.querySelector("[data-header]");
  if (!header) return;
  let lastY = window.scrollY;

  window.addEventListener(
    "scroll",
    Novarix.utils.throttle(function () {
      const y = window.scrollY;
      header.classList.toggle("is-scrolled", y > 12);
      if (header.dataset.sticky === "true") {
        header.classList.toggle("hide", y > lastY && y > 200);
      }
      lastY = y;
    }, 100),
    { passive: true }
  );
}

/* ---------- Mega menu (hover + keyboard) ---------- */
/* ---------- Hero shop dropdown (Shop Men / Shop Women) ---------- */
function initHeroShopDropdown() {
  const dropdowns = document.querySelectorAll("[data-hero-dropdown]");
  if (!dropdowns.length) return;

  const closeAll = () => {
    dropdowns.forEach((d) => {
      d.querySelector("[data-hero-dropdown-menu]")?.classList.remove("is-open");
      d.querySelector("[data-hero-dropdown-toggle]")?.setAttribute("aria-expanded", "false");
    });
  };

  dropdowns.forEach((dropdown) => {
    const toggle = dropdown.querySelector("[data-hero-dropdown-toggle]");
    const menu = dropdown.querySelector("[data-hero-dropdown-menu]");
    if (!toggle || !menu) return;

    toggle.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const isOpen = menu.classList.contains("is-open");
      closeAll();
      if (!isOpen) {
        menu.classList.add("is-open");
        toggle.setAttribute("aria-expanded", "true");
      }
    });
  });

  document.addEventListener("click", (e) => {
    if (![...dropdowns].some((d) => d.contains(e.target))) closeAll();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeAll();
  });
}

function initMegaMenu() {
  const items = document.querySelectorAll(".main-nav__item[data-nav-item]");
  if (!items.length) return;

  items.forEach((item) => {
    const link = item.querySelector(":scope > .main-nav__link");
    let closeTimer;

    const open = () => {
      clearTimeout(closeTimer);
      items.forEach((i) => {
        i.classList.remove("is-open");
        i.querySelector(":scope > .main-nav__link")?.setAttribute("aria-expanded", "false");
      });
      if (item.querySelector(".mega-menu")) {
        item.classList.add("is-open");
        link?.setAttribute("aria-expanded", "true");
      }
    };
    const close = () => {
      closeTimer = setTimeout(() => {
        item.classList.remove("is-open");
        link?.setAttribute("aria-expanded", "false");
      }, 120);
    };

    if (item.querySelector(".mega-menu")) {
      item.addEventListener("mouseenter", open);
      item.addEventListener("mouseleave", close);
      item.addEventListener("focusin", open);
      item.addEventListener("focusout", (e) => {
        if (!item.contains(e.relatedTarget)) close();
      });
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      items.forEach((i) => {
        i.classList.remove("is-open");
        i.querySelector(":scope > .main-nav__link")?.setAttribute("aria-expanded", "false");
      });
    }
  });
}

/* ---------- Mobile nav drawer ---------- */
function initMobileNav() {
  const openBtn = document.querySelector("[data-mobile-nav-open]");
  const closeBtn = document.querySelector("[data-mobile-nav-close]");
  const drawer = document.querySelector("[data-mobile-nav]");
  const overlay = document.querySelector('[data-overlay="mobile-nav"]');
  if (!drawer) return;

  const open = () => {
    Novarix.openDrawer(drawer, '[data-overlay="mobile-nav"]');
    openBtn?.setAttribute("aria-expanded", "true");
  };
  const close = () => {
    Novarix.closeDrawer(drawer, '[data-overlay="mobile-nav"]');
    openBtn?.setAttribute("aria-expanded", "false");
  };

  openBtn?.addEventListener("click", open);
  closeBtn?.addEventListener("click", close);
  overlay?.addEventListener("click", close);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && drawer.classList.contains("is-open")) close();
  });

  drawer.querySelectorAll("[data-accordion-toggle]").forEach((toggle) => {
    toggle.addEventListener("click", () => {
      const acc = toggle.closest("[data-accordion]");
      const panel = acc.querySelector("[data-accordion-panel]");
      const isOpen = acc.classList.contains("is-open");

      drawer.querySelectorAll("[data-accordion]").forEach((a) => {
        a.classList.remove("is-open");
        a.querySelector("[data-accordion-panel]").style.maxHeight = null;
      });

      if (!isOpen) {
        acc.classList.add("is-open");
        panel.style.maxHeight = panel.scrollHeight + "px";
      }
    });
  });
}

/* =========================================================
   Module 05 — Product Page
   ========================================================= */

/* ---------- Variant picker (color / size / quantity / add to cart) ---------- */
function initProductForm() {
  document.querySelectorAll("[data-product-form]").forEach((form) => {
    const variantsEl = form.querySelector("[data-product-variants]");
    if (!variantsEl) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsEl.textContent);
    } catch (e) {
      return;
    }

    const variantIdInput = form.querySelector("[data-variant-id]");
    const addBtn = form.querySelector("[data-add-to-cart]");
    const addBtnText = form.querySelector("[data-add-to-cart-text]");
    const priceWrap = document.querySelector(".product-info__price");

    function getSelections() {
      const selections = [];
      form.querySelectorAll("[data-swatch-row], [data-size-row]").forEach((row) => {
        const idx = parseInt(row.dataset.optionIndex, 10);
        const selected = row.querySelector(".is-selected");
        selections[idx] = selected ? selected.dataset.value : null;
      });
      return selections;
    }

    function findVariant(selections) {
      return variants.find((v) => selections.every((val, i) => val == null || v.options[i] === val));
    }

    function updateAvailability(selections) {
      form.querySelectorAll("[data-swatch-row] .swatch-lg, [data-size-row] .size-btn").forEach((btn) => {
        const idx = parseInt(btn.dataset.optionIndex, 10);
        const test = selections.slice();
        test[idx] = btn.dataset.value;
        const match = variants.find((v) => test.every((val, i) => val == null || v.options[i] === val));
        btn.classList.toggle("is-unavailable", !!match && !match.available);
      });
    }

    function updateGallery(variant) {
      if (!variant || !variant.featured_image) return;
      const gallery = document.querySelector("[data-gallery]");
      if (!gallery) return;
      const mediaId = variant.featured_image.id;
      const targetImg = gallery.querySelector('.gallery__main-img[data-media-id="' + mediaId + '"]');
      const targetThumb = gallery.querySelector('.gallery__thumb[data-media-id="' + mediaId + '"]');
      if (targetImg) {
        gallery.querySelectorAll(".gallery__main-img").forEach((img) => img.classList.remove("is-active"));
        targetImg.classList.add("is-active");
      }
      if (targetThumb) {
        gallery.querySelectorAll(".gallery__thumb").forEach((t) => t.classList.remove("is-active"));
        targetThumb.classList.add("is-active");
      }
    }

    const payContinueBtn = form.querySelector("[data-pay-continue]");

    function updateUI(variant) {
      if (!variant) {
        if (addBtn) addBtn.disabled = true;
        if (payContinueBtn) payContinueBtn.disabled = true;
        if (addBtnText) addBtnText.textContent = "Unavailable";
        return;
      }
      if (variantIdInput) variantIdInput.value = variant.id;
      if (addBtn) addBtn.disabled = !variant.available;
      if (payContinueBtn) payContinueBtn.disabled = !variant.available;
      if (addBtnText) addBtnText.textContent = variant.available ? "Add to Cart" : "Sold Out";

      if (priceWrap) {
        const isSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        priceWrap.innerHTML = isSale
          ? '<span class="price--sale">' + Novarix.utils.formatMoney(variant.price, Novarix.moneyFormat) + '</span><span class="price--compare">' + Novarix.utils.formatMoney(variant.compare_at_price, Novarix.moneyFormat) + "</span>"
          : '<span class="price--regular">' + Novarix.utils.formatMoney(variant.price, Novarix.moneyFormat) + "</span>";
      }

      if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
    }

    form.querySelectorAll("[data-swatch-row] .swatch-lg, [data-size-row] .size-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const row = btn.closest("[data-swatch-row], [data-size-row]");
        row.querySelectorAll(".swatch-lg, .size-btn").forEach((b) => b.classList.remove("is-selected"));
        btn.classList.add("is-selected");

        const labelTarget = row.closest(".option-group").querySelector("[data-selected-value]");
        if (labelTarget) labelTarget.textContent = btn.dataset.value;

        const selections = getSelections();
        const variant = findVariant(selections);
        updateUI(variant);
        updateAvailability(selections);
        updateGallery(variant);
      });
    });

    updateAvailability(getSelections());
  });
}

/* ---------- Payment method picker (preference only — Shopify checkout decides what is offered) ---------- */
function initPaymentSelector() {
  // Tag-name fragments of the wallet buttons Shopify renders inside its dynamic checkout button.
  const WALLET_TAGS = { shopify_pay: "shop-pay", paypal: "paypal", apple_pay: "apple-pay", google_pay: "google-pay" };

  document.querySelectorAll("[data-pay-select]").forEach((group) => {
    const form = group.closest("[data-product-form]");
    const continueBtn = form?.querySelector("[data-pay-continue]");
    const continueText = form?.querySelector("[data-pay-continue-text]");
    const dynamicWrap = form?.querySelector("[data-pay-dynamic]");
    const note = form?.querySelector("[data-pay-note]");
    if (!continueBtn || !continueText || !note) return;

    group.hidden = false;

    function offeredWallet(type) {
      const fragment = WALLET_TAGS[type];
      if (!fragment || !dynamicWrap) return false;
      return Array.from(dynamicWrap.querySelectorAll("*")).some((el) => el.tagName.toLowerCase().includes(fragment));
    }

    group.addEventListener("change", (e) => {
      const input = e.target.closest('input[type="radio"]');
      if (!input) return;
      group.querySelectorAll(".pay-option").forEach((opt) => opt.classList.toggle("is-selected", opt.contains(input)));

      const isCard = input.dataset.payKind === "card";
      const label = input.dataset.payLabel;
      // Shopify picks which wallet its button shows; the theme can't switch it. Show the genuine
      // button only when it matches the choice, otherwise fall back to our neutral checkout button.
      const walletOffered = !isCard && offeredWallet(input.value);
      continueText.textContent = isCard ? "Pay by Card at Checkout" : "Continue to Checkout";
      continueBtn.hidden = walletOffered;
      if (dynamicWrap) dynamicWrap.hidden = !walletOffered;

      if (isCard) {
        note.textContent = "You'll enter your " + label + " card details on our secure checkout. You can still change your payment method there.";
      } else if (walletOffered) {
        note.textContent = "Use the " + label + " button above to check out.";
      } else {
        note.textContent = label + " isn't available as a one-tap button on this device or browser. Continue to checkout, where " + label + " is offered when it's supported for your device and account.";
      }
    });

    continueBtn.addEventListener("click", () => {
      if (continueBtn.disabled) return;

      // Shopify's own "More payment options" link: checks out the selected variant + quantity.
      const official = dynamicWrap?.querySelector(".shopify-payment-button__more-options");
      if (official) {
        official.click();
        return;
      }

      continueBtn.disabled = true;
      fetch(Novarix.routes.cartAddUrl + ".js", { method: "POST", headers: { Accept: "application/json" }, body: new FormData(form) })
        .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
        .then((result) => {
          if (!result.ok) {
            note.textContent = result.data.description || result.data.message || "Couldn't start checkout. Please try again.";
            continueBtn.disabled = false;
            return;
          }
          window.location.href = Novarix.routes.checkoutUrl;
        })
        .catch(() => {
          note.textContent = "Something went wrong. Please try again.";
          continueBtn.disabled = false;
        });
    });

    // Back/forward cache can restore the page with the button still disabled.
    window.addEventListener("pageshow", (e) => {
      if (e.persisted) continueBtn.disabled = !!form.querySelector("[data-add-to-cart]")?.disabled;
    });
  });
}

/* ---------- Gallery thumbnails (manual click, independent of variant swap) ---------- */
function initGalleryThumbs() {
  document.querySelectorAll("[data-gallery]").forEach((gallery) => {
    gallery.querySelectorAll(".gallery__thumb").forEach((thumb) => {
      thumb.addEventListener("click", () => {
        const mediaId = thumb.dataset.mediaId;
        gallery.querySelectorAll(".gallery__thumb").forEach((t) => t.classList.remove("is-active"));
        thumb.classList.add("is-active");
        gallery.querySelectorAll(".gallery__main-img").forEach((img) => img.classList.remove("is-active"));
        const target = gallery.querySelector('.gallery__main-img[data-media-id="' + mediaId + '"]');
        target?.classList.add("is-active");
      });
    });
  });
}

/* ---------- Quantity selectors ---------- */
function initQuantitySelectors() {
  document.querySelectorAll("[data-qty-selector]").forEach((sel) => {
    const input = sel.querySelector("[data-qty-input]");
    sel.querySelector('[data-qty="minus"]')?.addEventListener("click", () => {
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
    });
    sel.querySelector('[data-qty="plus"]')?.addEventListener("click", () => {
      input.value = (parseInt(input.value, 10) || 1) + 1;
    });
  });
}

/* ---------- Product page accordion (description / details / shipping / returns) ---------- */
function initAccordions() {
  document.querySelectorAll(".accordion-item__header").forEach((header) => {
    header.addEventListener("click", () => {
      const item = header.closest(".accordion-item");
      const body = item.querySelector("[data-accordion-body]");
      const isOpen = item.classList.contains("is-open");
      isOpen ? item.classList.remove("is-open") : item.classList.add("is-open");
      body.style.maxHeight = isOpen ? null : body.scrollHeight + "px";
    });
  });
}

/* ---------- Sticky add-to-cart bar ---------- */
function initStickyAddToCart() {
  const sticky = document.querySelector("[data-sticky-atc]");
  const anchor = document.querySelector("[data-atc-anchor]");
  if (!sticky || !anchor || !("IntersectionObserver" in window)) return;

  const observer = new IntersectionObserver(([entry]) => sticky.classList.toggle("is-visible", !entry.isIntersecting), {
    rootMargin: "-120px 0px 0px 0px"
  });
  observer.observe(anchor);

  sticky.querySelector("[data-sticky-add-to-cart]")?.addEventListener("click", () => {
    document.querySelector("[data-add-to-cart]")?.click();
  });
}

/* ---------- Size guide modal ---------- */
function initSizeGuide() {
  const modal = document.querySelector("[data-size-guide-modal]");
  if (!modal) return;
  document.querySelectorAll("[data-size-guide-open]").forEach((btn) => btn.addEventListener("click", () => modal.classList.add("is-open")));
  modal.querySelectorAll("[data-size-guide-close]").forEach((btn) => btn.addEventListener("click", () => modal.classList.remove("is-open")));
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") modal.classList.remove("is-open");
  });
}

/* ---------- Product recommendations (native Shopify recommendations endpoint) ---------- */
function initProductRecommendations() {
  document.querySelectorAll("[data-related-products]").forEach((section) => {
    const url = section.dataset.recommendationsUrl;
    if (!url) return;
    fetch(url)
      .then((res) => res.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        const newContent = doc.querySelector("[data-related-products]");
        if (newContent && newContent.innerHTML.trim()) {
          section.innerHTML = newContent.innerHTML;
        } else {
          section.remove();
        }
      })
      .catch(() => section.remove());
  });
}

/* ---------- Recently viewed (localStorage + public product .js endpoint) ---------- */
function initRecentlyViewed() {
  const KEY = "novarix_recently_viewed";
  const currentEl = document.querySelector("[data-current-product]");
  if (!currentEl) return;

  let current;
  try {
    current = JSON.parse(currentEl.textContent);
  } catch (e) {
    return;
  }

  let viewed = [];
  try {
    viewed = JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch (e) {
    viewed = [];
  }

  const others = viewed.filter((h) => h !== current.handle).slice(0, 4);

  viewed = viewed.filter((h) => h !== current.handle);
  viewed.unshift(current.handle);
  localStorage.setItem(KEY, JSON.stringify(viewed.slice(0, 8)));

  const container = document.querySelector("[data-recently-viewed]");
  const grid = document.querySelector("[data-recently-viewed-grid]");
  if (!others.length || !container || !grid) return;

  Promise.all(
    others.map((handle) =>
      fetch("/products/" + handle + ".js")
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
    )
  ).then((products) => {
    products = products.filter(Boolean);
    if (!products.length) return;
    grid.innerHTML = products.map(renderClientProductCard).join("");
    container.style.display = "";
  });
}

function renderClientProductCard(p) {
  const image = p.featured_image || (p.images && p.images[0]) || "";
  const onSale = p.compare_at_price && p.compare_at_price > p.price;
  const priceHtml = onSale
    ? '<span class="price--sale">' + Novarix.utils.formatMoney(p.price, Novarix.moneyFormat) + '</span><span class="price--compare">' + Novarix.utils.formatMoney(p.compare_at_price, Novarix.moneyFormat) + "</span>"
    : '<span class="price--regular">' + Novarix.utils.formatMoney(p.price, Novarix.moneyFormat) + "</span>";

  return (
    '<div class="product-card"><div class="product-card__media-wrap"><a href="' +
    p.url +
    '" class="product-card__media"><img class="main-img" src="' +
    image +
    '" alt="' +
    (p.title || "").replace(/"/g, "&quot;") +
    '" loading="lazy"></a></div><div class="product-card__info"><a href="' +
    p.url +
    '"><h3 class="product-card__name">' +
    p.title +
    '</h3></a><div class="price">' +
    priceHtml +
    "</div></div></div>"
  );
}

/* =========================================================
   Module 08 — Countdown timer (promotional banner / flash sale)
   ========================================================= */
function initCountdown() {
  document.querySelectorAll("[data-countdown]").forEach(function (el) {
    var end;
    if (el.dataset.countdownMode === "date") {
      var dateStr = el.dataset.countdownDate;
      if (!dateStr) {
        el.style.display = "none";
        return;
      }
      end = new Date(dateStr.replace(" ", "T")).getTime();
    } else {
      var hours = parseFloat(el.dataset.countdownHours) || 24;
      var storageKey = "novarix_countdown_" + (el.dataset.countdownId || "default");
      var stored = null;
      try {
        stored = sessionStorage.getItem(storageKey);
      } catch (e) {
        stored = null;
      }
      if (stored && !isNaN(parseInt(stored, 10)) && parseInt(stored, 10) > Date.now()) {
        end = parseInt(stored, 10);
      } else {
        end = Date.now() + hours * 3600000;
        try {
          sessionStorage.setItem(storageKey, String(end));
        } catch (e) {}
      }
    }
    if (isNaN(end)) {
      el.style.display = "none";
      return;
    }

    var dd = el.querySelector('[data-unit="days"]');
    var hh = el.querySelector('[data-unit="hours"]');
    var mm = el.querySelector('[data-unit="minutes"]');
    var ss = el.querySelector('[data-unit="seconds"]');
    var timer;

    function pad(n) {
      return String(n).padStart(2, "0");
    }

    function tick() {
      var diff = end - Date.now();
      if (diff <= 0) {
        clearInterval(timer);
        el.classList.add("is-expired");
        if (dd) dd.textContent = "00";
        if (hh) hh.textContent = "00";
        if (mm) mm.textContent = "00";
        if (ss) ss.textContent = "00";
        return;
      }
      var days = Math.floor(diff / 86400000);
      var hours = Math.floor((diff % 86400000) / 3600000);
      var mins = Math.floor((diff % 3600000) / 60000);
      var secs = Math.floor((diff % 60000) / 1000);
      if (dd) dd.textContent = pad(days);
      if (hh) hh.textContent = pad(hours);
      if (mm) mm.textContent = pad(mins);
      if (ss) ss.textContent = pad(secs);
    }

    tick();
    timer = setInterval(tick, 1000);
  });
}

/* =========================================================
   Shop Men / Shop Women — collection grid interactions
   ========================================================= */

/* ---------- Product card color swatches (select + swap image) ---------- */
function initProductCardSwatches() {
  document.querySelectorAll("[data-color-swatches]").forEach((wrap) => {
    wrap.querySelectorAll("[data-swatch-btn]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        wrap.querySelectorAll("[data-swatch-btn]").forEach((b) => b.classList.remove("is-selected"));
        btn.classList.add("is-selected");

        const card = wrap.closest("[data-product-card]");
        const mainImg = card?.querySelector("[data-card-main-img]");
        const swatchImage = btn.dataset.swatchImage;
        if (mainImg && swatchImage) mainImg.src = swatchImage;
      });
    });
  });
}

/* ---------- Quick add inline size picker (collection grid) ---------- */
function initQuickAddSizePicker() {
  document.querySelectorAll("[data-quickadd-widget]").forEach((widget) => {
    const trigger = widget.querySelector("[data-quickadd-trigger]");
    const panel = widget.querySelector("[data-quickadd-panel]");
    const variantsEl = widget.querySelector("[data-quickadd-variants]");
    if (!trigger || !panel || !variantsEl) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsEl.textContent);
    } catch (e) {
      return;
    }

    const sizeIndex = parseInt(trigger.dataset.sizeIndex, 10);
    const colorIndexRaw = trigger.dataset.colorIndex;
    const colorIndex = colorIndexRaw !== undefined ? parseInt(colorIndexRaw, 10) : -1;
    const variantInput = widget.querySelector("[data-quickadd-variant-input]");
    const form = widget.querySelector("[data-quick-add-form]");
    const card = widget.closest("[data-product-card]");

    function getSelectedColor() {
      if (colorIndex < 0) return null;
      const selected = card?.querySelector("[data-color-swatches] .is-selected");
      return selected ? selected.dataset.swatchValue : null;
    }

    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const willOpen = !panel.classList.contains("is-open");
      document.querySelectorAll("[data-quickadd-panel].is-open").forEach((p) => p.classList.remove("is-open"));
      if (willOpen) panel.classList.add("is-open");
    });

    panel.querySelectorAll("[data-size-value]").forEach((sizeBtn) => {
      sizeBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        const selectedColor = getSelectedColor();
        const sizeVal = sizeBtn.dataset.sizeValue;
        const match = variants.find((v) => {
          const sizeMatches = v.options[sizeIndex] === sizeVal;
          const colorMatches = colorIndex < 0 || selectedColor == null || v.options[colorIndex] === selectedColor;
          return sizeMatches && colorMatches;
        });

        if (!match || !match.available) {
          quickAddToast("This size is currently unavailable.");
          return;
        }

        variantInput.value = match.id;
        panel.classList.remove("is-open");
        if (form.requestSubmit) form.requestSubmit();
        else form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
      });
    });
  });

  document.addEventListener("click", (e) => {
    if (!e.target.closest("[data-quickadd-widget]")) {
      document.querySelectorAll("[data-quickadd-panel].is-open").forEach((p) => p.classList.remove("is-open"));
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      document.querySelectorAll("[data-quickadd-panel].is-open").forEach((p) => p.classList.remove("is-open"));
    }
  });
}

function quickAddToast(msg) {
  let toast = document.querySelector(".toast");
  if (!toast) {
    toast = document.createElement("div");
    toast.className = "toast";
    document.body.appendChild(toast);
  }
  toast.textContent = msg;
  toast.classList.add("is-visible");
  clearTimeout(toast._novarixQaTimer);
  toast._novarixQaTimer = setTimeout(() => toast.classList.remove("is-visible"), 2400);
}

/* ---------- Load more (collection grid pagination via Section Rendering API) ---------- */
function initLoadMore() {
  document.querySelectorAll("[data-load-more-wrap]").forEach((wrap) => {
    const btn = wrap.querySelector("[data-load-more-btn]");
    if (!btn) return;

    btn.addEventListener("click", () => {
      const nextUrl = wrap.dataset.nextUrl;
      const sectionId = wrap.dataset.sectionId;
      if (!nextUrl || !sectionId) return;

      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = "Loading...";

      const separator = nextUrl.indexOf("?") > -1 ? "&" : "?";
      const url = nextUrl + separator + "section_id=" + encodeURIComponent(sectionId);

      fetch(url)
        .then((res) => res.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const newGrid = doc.querySelector("[data-collection-grid]");
          const newWrap = doc.querySelector("[data-load-more-wrap]");
          const currentGrid = wrap.parentElement.querySelector("[data-collection-grid]");

          if (newGrid && currentGrid) {
            Array.prototype.slice.call(newGrid.children).forEach((child) => {
              currentGrid.appendChild(child);
            });
          }

          if (newWrap && newWrap.dataset.nextUrl) {
            wrap.dataset.nextUrl = newWrap.dataset.nextUrl;
            btn.disabled = false;
            btn.textContent = originalText;
          } else {
            wrap.remove();
          }

          initProductCardSwatches();
          initQuickAddSizePicker();
          initWishlist();
        })
        .catch(() => {
          btn.disabled = false;
          btn.textContent = originalText;
        });
    });
  });
}
